import { questionUsesFreeText } from "./formSelection";
import {
  normalizeDateInput,
  normalizeMeasurementInput,
  PROFILE_MEASUREMENT_LIMITS,
} from "../validation/controlledInputs";

const MAX_PROFILE_AGE_YEARS = 120;

function parseIsoDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(String(value || ""));
  if (!match) {
    return null;
  }

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return date;
}

function oldestAllowedDate() {
  const today = new Date();
  return new Date(
    Date.UTC(
      today.getUTCFullYear() - MAX_PROFILE_AGE_YEARS,
      today.getUTCMonth(),
      today.getUTCDate(),
    ),
  );
}

function todayDateUtc() {
  const today = new Date();
  return new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );
}

function validateMeasurementQuestion(questionId, value) {
  const normalizedValue = normalizeMeasurementInput(value, {
    fieldName: questionId,
  });

  if (normalizedValue === "") {
    return {
      valid: false,
      messageKey: "form.reqFieldsAlert",
    };
  }

  const parsedValue = Number(normalizedValue);
  if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
    return {
      valid: false,
      messageKey: "form.validation.numberPositive",
    };
  }

  const limits = PROFILE_MEASUREMENT_LIMITS[questionId];
  if (
    limits &&
    (parsedValue < limits.min || parsedValue > limits.max)
  ) {
    return {
      valid: false,
      messageKey: limits.messageKey,
    };
  }

  return { valid: true, messageKey: null };
}

export const getQuestionValidationResult = (question, formData) => {
  const value = formData[question.id];
  const requiresFreeText = questionUsesFreeText(question, value);
  const freeTextValue = requiresFreeText
    ? formData?.[question.freeTextFieldId]
    : null;

  if (!question.required) {
    return { valid: true, messageKey: null };
  }

  switch (question.fieldType) {
    case "boolean-matrix": {
      if (!Array.isArray(question.rows) || question.rows.length === 0) {
        return { valid: true, messageKey: null };
      }

      if (value == null || typeof value !== "object") {
        return { valid: false, messageKey: "form.reqFieldsAlert" };
      }

      const allAnswered = question.rows.every((row) => {
        const rowValue = value[row.id];
        return rowValue !== undefined && rowValue !== null && String(rowValue) !== "";
      });

      return {
        valid: allAnswered,
        messageKey: allAnswered ? null : "form.reqFieldsAlert",
      };
    }

    case "range":
      return {
        valid: value !== undefined && value !== null && value !== "",
        messageKey:
          value !== undefined && value !== null && value !== ""
            ? null
            : "form.reqFieldsAlert",
      };
    case "episode-timing": {
      const timingValue = value && typeof value === "object" ? value : null;

      if (!timingValue) {
        return { valid: false, messageKey: "form.reqFieldsAlert" };
      }

      if (timingValue.unknown === true) {
        return { valid: true, messageKey: null };
      }

      const normalizedDate = normalizeDateInput(
        timingValue.approximateOnsetDate,
      );
      const parsedDate = parseIsoDate(normalizedDate);

      if (normalizedDate === "") {
        return { valid: false, messageKey: "form.reqFieldsAlert" };
      }

      if (!parsedDate || parsedDate > todayDateUtc()) {
        return { valid: false, messageKey: "form.validation.dateInvalid" };
      }

      const durationDays = Number(timingValue.durationDays);
      if (!Number.isInteger(durationDays) || durationDays < 1) {
        return {
          valid: false,
          messageKey: "form.validation.numberPositive",
        };
      }

      if (timingValue.ongoing !== true && timingValue.ongoing !== false) {
        return { valid: false, messageKey: "form.reqFieldsAlert" };
      }

      return { valid: true, messageKey: null };
    }
    case "checkbox-group":
      if (!Array.isArray(value) || value.length === 0) {
        return { valid: false, messageKey: "form.reqFieldsAlert" };
      }

      if (
        requiresFreeText &&
        (freeTextValue === undefined ||
          freeTextValue === null ||
          String(freeTextValue).trim() === "")
      ) {
        return { valid: false, messageKey: "form.reqFieldsAlert" };
      }

      return { valid: true, messageKey: null };
    case "date": {
      const normalizedDate = normalizeDateInput(value);
      const parsedDate = parseIsoDate(normalizedDate);

      if (normalizedDate === "") {
        return { valid: false, messageKey: "form.reqFieldsAlert" };
      }

      if (!parsedDate || parsedDate > todayDateUtc()) {
        return { valid: false, messageKey: "form.validation.dateInvalid" };
      }

      if (parsedDate < oldestAllowedDate()) {
        return {
          valid: false,
          messageKey: "form.validation.dateOfBirthTooOld",
        };
      }

      return { valid: true, messageKey: null };
    }
    case "number": {
      if (
        question.id === "height" ||
        question.id === "weight" ||
        question.id === "chestCircumference"
      ) {
        return validateMeasurementQuestion(question.id, value);
      }

      return {
        valid: value !== undefined && value !== null && String(value) !== "",
        messageKey:
          value !== undefined && value !== null && String(value) !== ""
            ? null
            : "form.reqFieldsAlert",
      };
    }
    case "radio":
    case "select":
    case "text":
    case "textarea":
    default:
      if (value === undefined || value === null || String(value) === "") {
        return { valid: false, messageKey: "form.reqFieldsAlert" };
      }

      if (
        requiresFreeText &&
        (freeTextValue === undefined ||
          freeTextValue === null ||
          String(freeTextValue).trim() === "")
      ) {
        return { valid: false, messageKey: "form.reqFieldsAlert" };
      }

      return { valid: true, messageKey: null };
  }
};

export const isQuestionValid = (question, formData) => {
  return getQuestionValidationResult(question, formData).valid;
};
